// Pacing math for the advanced event tracker. Pure functions only: no Vue,
// no Element, no network. The baseline is derived from history rows returned
// by the draft-puzzles lambda (one row per puzzle instance of a finished
// event, elapsed values already computed in SQL as whole seconds).

export const SAMPLE_LIMIT = 100
export const MIN_SAMPLE = 5
export const TICK_MS = 1000
// History guard: instances longer than this are treated as bad data (a draft
// finished days later, a start time typed on the wrong day) and dropped.
export const MAX_INSTANCE_SECONDS = 6 * 3600

const NEAR_TYPICAL_SECONDS = 30

// Accepts a Date, or a naive local datetime string in either
// "YYYY-MM-DD HH:MM[:SS]" or "YYYY-MM-DDTHH:MM:SS[.mmm][Z]" form. The tracker
// writes wall-clock local times, so the string is always read as local time
// and never handed to the Date constructor as a string.
export function toLocalDate(value) {
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value
  }
  if (typeof value !== 'string' || value.length < 16) {
    return null
  }
  const sep = value.includes('T') ? 'T' : ' '
  const [datePart, timePartRaw] = value.split(sep)
  if (!datePart || !timePartRaw) {
    return null
  }
  const timePart = timePartRaw.replace('Z', '').split('.')[0]
  const [y, m, d] = datePart.split('-').map(n => parseInt(n, 10))
  const [h, mi, s] = timePart.split(':').map(n => parseInt(n, 10))
  if ([y, m, d, h, mi].some(n => isNaN(n))) {
    return null
  }
  const date = new Date(y, m - 1, d, h, mi, isNaN(s) ? 0 : s)
  return isNaN(date.getTime()) ? null : date
}

// Linear interpolation percentile over an unsorted numeric array.
export function percentile(values, q) {
  const sorted = values.filter(v => typeof v === 'number' && !isNaN(v)).sort((a, b) => a - b)
  if (sorted.length === 0) {
    return null
  }
  const pos = (sorted.length - 1) * q
  const lower = Math.floor(pos)
  const upper = Math.ceil(pos)
  if (lower === upper) {
    return sorted[lower]
  }
  return sorted[lower] + (sorted[upper] - sorted[lower]) * (pos - lower)
}

function band(values) {
  return {
    n: values.length,
    p25: percentile(values, 0.25),
    p50: percentile(values, 0.5),
    p75: percentile(values, 0.75)
  }
}

function secondsBetween(startDate, laterDate) {
  return Math.max(0, Math.floor((laterDate.getTime() - startDate.getTime()) / 1000))
}

function isTopLevel(puzzle) {
  return !puzzle.parent_id
}

function isSolved(puzzle) {
  return Boolean(puzzle.solve_time) && !puzzle.did_not_finish
}

// rows: [{ game_instance_id, total_seconds, completed, number_of_hints,
//          puzzle_id, solve_seconds, hints_given_count }]
export function buildBaseline(rows) {
  const instances = new Map()
  for (const row of rows || []) {
    const total = Number(row.total_seconds)
    if (!(total > 0) || total > MAX_INSTANCE_SECONDS) {
      continue
    }
    if (!instances.has(row.game_instance_id)) {
      instances.set(row.game_instance_id, {
        total,
        hints: Number(row.number_of_hints) || 0,
        solves: []
      })
    }
    const solve = row.solve_seconds === null || row.solve_seconds === undefined ? null : Number(row.solve_seconds)
    instances.get(row.game_instance_id).solves.push({
      puzzleId: Number(row.puzzle_id),
      seconds: solve !== null && solve > 0 && solve <= total ? solve : null,
      hints: Number(row.hints_given_count) || 0
    })
  }

  const perPuzzleValues = new Map()
  const perPuzzleHints = new Map()
  const atCountT = []
  const atCountRemaining = []
  const totals = []
  const hintTotals = []

  for (const inst of instances.values()) {
    totals.push(inst.total)
    hintTotals.push(inst.hints)
    for (const s of inst.solves) {
      if (!perPuzzleHints.has(s.puzzleId)) {
        perPuzzleHints.set(s.puzzleId, [])
        perPuzzleValues.set(s.puzzleId, [])
      }
      perPuzzleHints.get(s.puzzleId).push(s.hints)
      if (s.seconds !== null) {
        perPuzzleValues.get(s.puzzleId).push(s.seconds)
      }
    }
    const solved = inst.solves.map(s => s.seconds).filter(v => v !== null).sort((a, b) => a - b)
    for (let k = 0; k <= solved.length; k++) {
      const tK = k === 0 ? 0 : solved[k - 1]
      if (!atCountT[k]) {
        atCountT[k] = []
        atCountRemaining[k] = []
      }
      atCountT[k].push(tK)
      atCountRemaining[k].push(Math.max(0, inst.total - tK))
    }
  }

  const perPuzzle = {}
  for (const [puzzleId, values] of perPuzzleValues.entries()) {
    perPuzzle[puzzleId] = {
      ...band(values),
      hintsP50: percentile(perPuzzleHints.get(puzzleId), 0.5)
    }
  }

  const atCount = []
  for (let k = 0; k < atCountT.length; k++) {
    const t = band(atCountT[k])
    atCount[k] = {
      n: t.n,
      tP25: t.p25,
      tP50: t.p50,
      tP75: t.p75,
      remainingP50: percentile(atCountRemaining[k], 0.5)
    }
  }

  const sampleSize = instances.size
  return {
    source: 'history',
    sampleSize,
    enoughHistory: sampleSize >= MIN_SAMPLE,
    perPuzzle,
    atCount,
    totalP50: percentile(totals, 0.5),
    hintsTotalP50: percentile(hintTotals, 0.5)
  }
}

// Fallback when history is thin: spread the time limit evenly across the
// top-level objectives in position order.
export function evenSpreadBaseline(puzzles, limitSeconds, sampleSize = 0) {
  const topLevel = (puzzles || []).filter(isTopLevel).slice().sort((a, b) => a.position - b.position)
  const n = topLevel.length
  const limit = Number(limitSeconds) || 0
  const perPuzzle = {}
  const atCount = [{ n: 0, tP25: 0, tP50: 0, tP75: 0, remainingP50: limit }]
  topLevel.forEach((p, i) => {
    const p50 = n > 0 ? limit * (i + 1) / n : 0
    perPuzzle[p.source_puzzle_id] = { n: 0, p25: p50 * 0.8, p50, p75: p50 * 1.2, hintsP50: null }
    atCount[i + 1] = { n: 0, tP25: p50 * 0.8, tP50: p50, tP75: p50 * 1.2, remainingP50: Math.max(0, limit - p50) }
  })
  return {
    source: 'even_spread',
    sampleSize,
    enoughHistory: false,
    perPuzzle,
    atCount,
    totalP50: limit,
    hintsTotalP50: null
  }
}

function solvedAtFor(puzzle, startDate) {
  if (!isSolved(puzzle)) {
    return null
  }
  const solved = toLocalDate(puzzle.solve_time)
  return solved ? secondsBetween(startDate, solved) : null
}

// Unsolved objectives carry a band with a null delta: 'upcoming' until the
// fastest quarter of teams would have solved it (p25), 'approaching' until the
// slowest quarter would have (p75), then 'overdue' with a real delta. Solved
// objectives carry fast / typical / slow with a delta from p50. Consumers that
// only want solved comparisons gate on delta !== null.
function chipFor(entry, baseline, elapsed) {
  const stats = baseline.perPuzzle[entry.source_puzzle_id]
  if (!stats || stats.p50 === null || stats.p50 === undefined) {
    return { delta: null, band: null, typical: null }
  }
  const solvedAt = entry.solvedAt
  if (solvedAt === null) {
    if (entry.did_not_finish) {
      return { delta: null, band: null, typical: stats.p50 }
    }
    if (stats.p75 !== null && elapsed > stats.p75) {
      return { delta: elapsed - stats.p50, band: 'overdue', typical: stats.p50 }
    }
    if (stats.p25 !== null && elapsed > stats.p25) {
      return { delta: null, band: 'approaching', typical: stats.p50 }
    }
    return { delta: null, band: 'upcoming', typical: stats.p50 }
  }
  let which = 'typical'
  if (stats.p25 !== null && solvedAt < stats.p25) {
    which = 'fast'
  } else if (stats.p75 !== null && solvedAt > stats.p75) {
    which = 'slow'
  }
  return { delta: solvedAt - stats.p50, band: which, typical: stats.p50 }
}

// Core comparison. entries: [{ source_puzzle_id, name, position, parent_id,
// did_not_finish, hints, solvedAt (seconds since start or null) }]
export function pacingFromEntries({ baseline, entries, elapsed, limitSeconds }) {
  if (!baseline) {
    return null
  }
  const list = entries || []
  const perPuzzle = {}
  let hintsGiven = 0
  const topSolvedAt = []
  let topLevelTotal = 0

  for (const entry of list) {
    hintsGiven += parseInt(entry.hints, 10) || 0
    perPuzzle[entry.source_puzzle_id] = { solvedAt: entry.solvedAt, ...chipFor(entry, baseline, elapsed) }
    if (isTopLevel(entry)) {
      topLevelTotal++
      if (entry.solvedAt !== null) {
        topSolvedAt.push(entry.solvedAt)
      }
    }
  }

  const k = topSolvedAt.length
  const tK = k === 0 ? 0 : Math.max(...topSolvedAt)
  const here = baseline.atCount[k] || null
  const next = baseline.atCount[k + 1] || null

  let status = 'on_pace'
  if (topLevelTotal > 0 && k >= topLevelTotal) {
    status = 'finished'
  } else if (!here && !next) {
    status = 'unknown'
  } else {
    const behindNext = next && next.tP75 !== null && elapsed > next.tP75
    const behindHere = here && here.tP75 !== null && k > 0 && tK > here.tP75
    const aheadHere = here && here.tP25 !== null && k > 0 && tK < here.tP25
    if (behindNext || behindHere) {
      status = 'behind'
    } else if (aheadHere) {
      status = 'ahead'
    }
  }

  const remaining = here && here.remainingP50 !== null ? here.remainingP50 : (baseline.totalP50 !== null ? Math.max(0, baseline.totalP50 - elapsed) : null)
  const projectedFinish = remaining === null ? null : elapsed + remaining
  const limit = Number(limitSeconds) || 0
  const projectedDelta = projectedFinish === null || limit <= 0 ? null : projectedFinish - limit

  return {
    status,
    elapsed,
    solvedCount: k,
    topLevelTotal,
    projectedFinish,
    projectedDelta,
    perPuzzle,
    hints: { given: hintsGiven, typical: baseline.hintsTotalP50 }
  }
}

function entriesFromTrackerPuzzles(puzzles, start) {
  return (puzzles || []).map(p => ({
    source_puzzle_id: p.source_puzzle_id,
    name: p.name,
    position: p.position,
    parent_id: p.parent_id,
    did_not_finish: Boolean(p.did_not_finish),
    hints: p.hints_given_count,
    solvedAt: solvedAtFor(p, start)
  }))
}

// Live tracker: puzzles carry Date or local string solve times.
export function computePacing({ baseline, puzzles, startDate, now, limitSeconds }) {
  const start = toLocalDate(startDate)
  const current = toLocalDate(now) || new Date()
  if (!baseline || !start) {
    return null
  }
  const elapsed = secondsBetween(start, current)
  return pacingFromEntries({ baseline, entries: entriesFromTrackerPuzzles(puzzles, start), elapsed, limitSeconds })
}

function summaryFromEntries({ baseline, entries, totalSeconds, limitSeconds }) {
  const live = pacingFromEntries({ baseline, entries, elapsed: totalSeconds, limitSeconds })
  if (!live) {
    return null
  }
  const objectives = (entries || [])
    .filter(isTopLevel)
    .slice()
    .sort((a, b) => a.position - b.position)
    .map(e => {
      const entry = live.perPuzzle[e.source_puzzle_id] || { solvedAt: null, delta: null, band: null, typical: null }
      const band = entry.solvedAt === null ? null : entry.band
      return { name: e.name, solvedAt: entry.solvedAt, p50: entry.typical, delta: band ? entry.delta : null, band }
    })
  return {
    status: live.status,
    totalSeconds,
    totalP50: baseline.totalP50,
    totalDelta: baseline.totalP50 === null ? null : totalSeconds - baseline.totalP50,
    hints: live.hints,
    sampleSize: baseline.sampleSize,
    enoughHistory: baseline.enoughHistory,
    perPuzzle: live.perPuzzle,
    objectives
  }
}

// Review step: same tracker puzzles plus the end time.
export function summarizePacing({ baseline, puzzles, startDate, endDate, limitSeconds }) {
  const start = toLocalDate(startDate)
  const end = toLocalDate(endDate)
  if (!baseline || !start || !end) {
    return null
  }
  const totalSeconds = secondsBetween(start, end)
  return summaryFromEntries({ baseline, entries: entriesFromTrackerPuzzles(puzzles, start), totalSeconds, limitSeconds })
}

// History page: elapsed values already computed in SQL as whole seconds.
// rows: [{ puzzle_id, puzzle_name, puzzle_position, parent_id, is_completed,
//          solve_seconds, hints_given_count }]
export function summarizeHistoryInstance({ baseline, rows, totalSeconds, limitSeconds }) {
  if (!baseline || !(Number(totalSeconds) > 0)) {
    return null
  }
  const entries = (rows || []).map(r => {
    const solve = r.solve_seconds === null || r.solve_seconds === undefined ? null : Number(r.solve_seconds)
    const completed = Boolean(Number(r.is_completed))
    return {
      source_puzzle_id: Number(r.puzzle_id),
      name: r.puzzle_name,
      position: Number(r.puzzle_position) || 0,
      parent_id: r.parent_id,
      did_not_finish: !completed,
      hints: r.hints_given_count,
      solvedAt: completed && solve !== null && solve > 0 ? solve : null
    }
  })
  return summaryFromEntries({ baseline, entries, totalSeconds: Number(totalSeconds), limitSeconds })
}

export function formatDuration(seconds) {
  if (seconds === null || seconds === undefined || isNaN(seconds)) {
    return ''
  }
  const total = Math.max(0, Math.round(seconds))
  const minutes = Math.floor(total / 60)
  const secs = total % 60
  return minutes + ':' + (secs < 10 ? '0' : '') + secs
}

export function formatDelta(seconds) {
  if (seconds === null || seconds === undefined || isNaN(seconds)) {
    return ''
  }
  if (Math.abs(seconds) < NEAR_TYPICAL_SECONDS) {
    return 'on typical pace'
  }
  const magnitude = formatDuration(Math.abs(seconds))
  return seconds < 0 ? magnitude + ' faster than typical' : magnitude + ' slower than typical'
}

export function statusLabel(status) {
  switch (status) {
    case 'ahead': return 'Ahead'
    case 'on_pace': return 'On pace'
    case 'behind': return 'Behind'
    case 'finished': return 'Finished'
    default: return ''
  }
}
